import { computed, inject } from '@angular/core';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, Observable, pipe } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { ServerAction, ServerActionsStore, ServerActionType } from '@ess-front/shared/websockets';
import { BookingApiService } from '@app/users-area/api/booking.api.service';
import { BookingReadyServerAction } from '@app/utils/models/booking-ready-server-action.model';
import { BookingSimple } from '@app/utils/models/booking-simple.model';
import { TripApiService } from '@shared/api/trip-api.service';
import { SnackBarService } from '@shared/domain/snack-bar.service';
import { canNavigateTrip, getPreviousTrips, getRequestedTrips, getUpcomingTrips } from './user-trips-selectors';

interface UserTripsSignalState {
  hasLoadError$$: boolean;
  isLoading$$: boolean;
  trips$$: BookingSimple[];
}

const INITIAL_STATE: UserTripsSignalState = {
  hasLoadError$$: false,
  isLoading$$: true,
  trips$$: [],
};

function isBookingReadyServerAction(serverAction: ServerAction | null): serverAction is BookingReadyServerAction {
  return serverAction?.actionType === ServerActionType.BookingReady;
}

/**
 * Trips of the logged-in member, split into the members area sections (requested, upcoming, previous). Kept in
 * sync with the websocket: a BookingReady server action for a loaded trip refetches it and patches its public
 * status in place.
 */
export const UserTripsSignalStore = signalStore(
  { providedIn: 'root' },
  withState<UserTripsSignalState>(INITIAL_STATE),

  withComputed(({ isLoading$$, trips$$ }) => ({
    // Loading with nothing to show yet. A reload keeps the trips already shown (so a return to the page renders them
    // at full height and the router can restore the scroll) and only `isLoading$$` tells it is still refreshing.
    isInitialLoad$$: computed(() => isLoading$$() && !trips$$().length),
    previousTrips$$: computed(() => getPreviousTrips(trips$$())),
    requestedTrips$$: computed(() => getRequestedTrips(trips$$())),
    upcomingTrips$$: computed(() => getUpcomingTrips(trips$$())),
  })),

  withMethods(
    (
      store,
      // DI
      bookingApiService = inject(BookingApiService),
      snackBarService = inject(SnackBarService),
      tripApiService = inject(TripApiService),
    ) => ({
      // A failed load is flagged, so the view can tell it apart from a member with no trips. A failed refresh of trips
      // already shown keeps them on screen: only the toast reports it.
      loadTrips$(): Observable<void> {
        patchState(store, { hasLoadError$$: false, isLoading$$: true });
        return bookingApiService.getTrips$().pipe(
          tap(trips => patchState(store, { trips$$: trips })),
          map(() => undefined),
          catchError(() => {
            patchState(store, ({ trips$$ }) => ({ hasLoadError$$: !trips$$.length }));
            snackBarService.openToast({
              closable: true,
              feedback: 'danger',
              messageKey: marker('errors.genericRequestRetry'),
            });
            return EMPTY;
          }),
          finalize(() => patchState(store, { isLoading$$: false })),
        );
      },

      // mergeMap so BookingReady actions for different trips each get their refetch. The patch is applied
      // against the trips at response time, so an intervening load or patch is not overwritten.
      refreshTripOnBookingReady: rxMethod<ServerAction | null>(
        pipe(
          filter(isBookingReadyServerAction),
          map(serverAction => serverAction.payload.hash),
          filter(bookingHash => store.trips$$().some(bookingSimple => bookingSimple.hash === bookingHash)),
          mergeMap(bookingHash =>
            tripApiService.getTrip$(bookingHash).pipe(
              tap(({ publicStatus }) =>
                patchState(store, ({ trips$$ }) => ({
                  trips$$: trips$$.map(bookingSimple =>
                    bookingSimple.hash === bookingHash
                      ? { ...bookingSimple, canNavigate: canNavigateTrip(publicStatus.value), publicStatus }
                      : bookingSimple,
                  ),
                })),
              ),
              catchError(() => EMPTY),
            ),
          ),
        ),
      ),

      unloadTrips(): void {
        patchState(store, { trips$$: [] });
      },
    }),
  ),

  withHooks({
    onInit(store) {
      store.refreshTripOnBookingReady(inject(ServerActionsStore).lastServerAction);
    },
  }),
);
